import Bugsnag from '@bugsnag/js';
import { pick } from 'ramda';

import config from '@/config';

import type { BugsnagPluginAwsLambdaHandler } from '@bugsnag/plugin-aws-lambda';
import type { Client, Plugin } from '@bugsnag/js';
import type { NextApiRequest } from 'next/types';
import type { NextRequest } from 'next/server';

let bugsnagClient: Client;

export function startBugsnag() {
  if (process.env.NEXT_PHASE !== 'phase-production-build' && !bugsnagClient) {
    const plugins = process.env.NEXT_IS_SERVER
      ? // eslint-disable-next-line @typescript-eslint/no-var-requires
        [require('@bugsnag/plugin-aws-lambda') as Plugin]
      : [];
    bugsnagClient = Bugsnag.start({
      apiKey: config.bugsnag.apiKey,
      appVersion: process.env.NEXT_BUILD_ID,
      enabledReleaseStages: ['production', 'preview'],
      plugins,
      releaseStage: config.env,
    });
  }
}

export function getServerlessHandler() {
  if (!bugsnagClient) {
    startBugsnag();
  }
  return Bugsnag.getPlugin(
    'awsLambda'
  )?.createHandler() as BugsnagPluginAwsLambdaHandler;
}

export function reportError(
  error: Error,
  request: NextApiRequest | NextRequest
): Promise<void> {
  if (!bugsnagClient) {
    startBugsnag();
  }
  return new Promise((resolve) => {
    Bugsnag.notify(
      error,
      (event) => {
        event.context = `${request.method as string} ${request.url as string}`;
        event.severity = 'error';
        event.unhandled = true;
        event.clearMetadata('AWS Lambda context');
        event.addMetadata(
          'Request',
          pick(['body', 'headers', 'method', 'url', 'query'], request)
        );
      },
      () => {
        resolve();
      }
    );
  });
}

export const reportClientError = (
  error: Error,
  options?: {
    metadata?: Record<string, Record<string, unknown>>;
    severity?: 'error' | 'warning' | 'info';
    isUnhandled?: boolean;
  }
): Promise<void> => {
  const { isUnhandled, metadata, severity } = {
    isUnhandled: true,
    severity: 'error' as const,
    ...options,
  };

  if (!bugsnagClient) {
    startBugsnag();
  }
  return new Promise((resolve) => {
    Bugsnag.notify(
      error,
      (event) => {
        event.severity = severity;
        event.unhandled = isUnhandled;
        if (metadata) {
          Object.entries(metadata).forEach(([key, value]) =>
            event.addMetadata(key, value)
          );
        }
      },
      () => resolve()
    );
  });
};
